// QR Kiosk v2 — KV2-3 file pipeline hook (both v1 forms duplicated this; extracted once).
// FileReader → data-URI, 11 MB hard gate (MAX_FILE_SIZE_MB), add/remove, in-place
// rejected-file record. No count cap, no type allowlist by default (v1 parity — KV2-3-d).
// KV2-7 additive: an optional `opts` narrows to a max count + images-only whitelist
// (task photos, B5 SEC-REQ-B5-6) — existing 1–2-arg callers are byte-identical.
import { useState } from "react";
import { ServiceReportFile, ServiceReportFileTypeCode } from "../../models/serviceReportsModels";
import { MAX_FILE_SIZE_MB } from "../../../shared/models/models";

export interface RejectedFile {
  name: string;
  sizeMb: number;
}

export interface KioskFilesOptions {
  /** reject adds beyond this count (rejected-record, not silent). */
  maxFiles?: number;
  /** accept only image/jpeg|png|webp MIME (B5 whitelist). */
  imagesOnly?: boolean;
}

// v1 detectUrl helper, verbatim body.
function detectUrl(file: Blob | null): Promise<string | null> {
  return new Promise((resolve, reject) => {
    if (!file) {
      resolve(null);
      return;
    }
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = (error) => reject(error);
  });
}

export function useKioskFiles(
  typeCode: ServiceReportFileTypeCode,
  initial?: ServiceReportFile[],
  opts?: KioskFilesOptions
) {
  const [files, setFiles] = useState<ServiceReportFile[]>(initial ?? []);
  const [rejected, setRejected] = useState<RejectedFile | null>(null);

  const addFiles = async (list: FileList) => {
    setRejected(null); // clear at the start of each add batch
    const arr = Array.from(list);
    let count = files.length; // running count for the maxFiles cap across the batch
    for (const file of arr) {
      const reject = () => setRejected({ name: file.name, sizeMb: +(file.size / 1048576).toFixed(1) });
      if (opts?.maxFiles != null && count >= opts.maxFiles) {
        reject();
        continue; // over the count cap (KV2-7 task photos ≤3)
      }
      if (opts?.imagesOnly && !/^image\/(jpeg|png|webp)$/.test(file.type)) {
        reject();
        continue; // non-image (B5 whitelist; catches iOS HEIC as image/heic client-side)
      }
      if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
        reject();
        continue; // SKIP (v1 gate, 11 MB hard — matches B5's pre-decode cap)
      }
      const url = await detectUrl(file);
      if (url) {
        count += 1;
        setFiles((prev) => [...prev, { name: file.name, content: url, typeCode, size: file.size }]);
      }
    }
  };

  const removeFile = (index: number) => setFiles((prev) => prev.filter((_, i) => i !== index));
  const dismissRejected = () => setRejected(null);
  const resetFiles = (next: ServiceReportFile[]) => setFiles(next);

  return { files, addFiles, removeFile, rejected, dismissRejected, resetFiles };
}
